// createdItemApi.js
import axiosInstance from "./mainApi";
import store from "../store/store"; // Redux store importálása
import {
  addCreatedItem,
  addMoreCreatedItems,
  deleteCreatedItem,
  deleteMoreCreatedItems,
  updateCreatedItem
} from "../store/actions/objectStoreFunctions";

const duSeqByWork = new Map(); // workId -> utolsó kiosztott sorszám
const isFrontendOnly = (x) => x?.frontend === true;

function extractDuNumber(details) {
  if (details == null) return 0;
  const s = String(details).trim();
  const m = s.match(/\bD\s*0*(\d+)\b/i);
  return m ? parseInt(m[1], 10) : 0;
}

function tokenKey(details) {
  const s = String(details || "").trim();
  const m = s.match(/^du\s*:\s*(.+)$/i);
  if (!m) return null;
  return m[1].trim().toUpperCase(); // normalizált group kulcs
}
function ensureDuplungLabels(payloads) {
  if (!Array.isArray(payloads) || payloads.length === 0) return payloads;

  const wid = Number(payloads[0]?.work?.workId);
  if (!wid) return payloads;

  const st = store.getState() || {};
  const existing = (st.createdItems || []).filter(
    (it) => Number(it.work?.workId) === wid
  );

  // 1) induló max D a store-ból
  let max = duSeqByWork.get(wid) || 0;
  for (const it of existing) {
    const dn = extractDuNumber(it.details);
    if (dn > max) max = dn;
  }
  // 2) a batch explicit D-i is növelhetik a maxot
  for (const p of payloads) {
    const dn = extractDuNumber(p.details);
    if (!/^du\s*:/i.test(String(p.details || "")) && dn > max) max = dn;
  }

  // 3) tokenes (DU:...) tételek csoportosítása
  const groupToIdxs = new Map(); // groupKey -> indexek
  for (let i = 0; i < payloads.length; i++) {
    const gk = tokenKey(payloads[i].details);
    if (!gk) continue;
    if (!groupToIdxs.has(gk)) groupToIdxs.set(gk, []);
    groupToIdxs.get(gk).push(i);
  }

  // 4) csoportonként kiosztunk egy D-t (mindig ÚJAT, kivéve ha a batchben már van fix D)
  const groupToD = new Map();
  for (const [gk, idxs] of groupToIdxs.entries()) {
    let chosen = null;
    // ha a csoporton belül már van fix D, azt használjuk
    for (const i of idxs) {
      const dn = extractDuNumber(payloads[i].details);
      if (dn > 0) {
        chosen = `D${dn}`;
        if (dn > max) max = dn;
        break;
      }
    }
    if (!chosen) chosen = `D${++max}`;
    groupToD.set(gk, chosen);
  }

  // 5) átírjuk a tokenes tételek details-ét a kiosztott D-re
  const labeled = payloads.map((p) => {
    const gk = tokenKey(p.details);
    if (gk && groupToD.has(gk)) {
      return { ...p, details: groupToD.get(gk) };
    }
    return p; // nem tokeneshez nem nyúlunk
  });

  duSeqByWork.set(wid, max);
  return labeled;
}

const getAllCreatedItemsApi = async () => {
  try {
    const response = await axiosInstance.get("/created-itemss");
    console.log("Loading createdItems from server response: ", response);
    if (response.status === 200) {
      store.dispatch(addMoreCreatedItems(response.data)); // Dispatching to update Redux store
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching createdItems:", error);
    throw error;
  }
};

const getAllCreatedItemsForObjectApi = async (objectId) => {
  try {
    const response = await axiosInstance.get(
      `/created-items/object/${objectId}`
    );
    console.log(
      "Loading createdItems for object: ",
      objectId,
      " from server response: ",
      response
    );
    if (response.status === 200) {
      store.dispatch(addMoreCreatedItems(response.data)); // Dispatching to update Redux store
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching createdItems:", error);
    throw error;
  }
};

const getAllCreatedItemsForWorkApi = async (workId) => {
  try {
    const response = await axiosInstance.get(`/created-items/work/${workId}`);
    console.log(
      "Loading createdItems for work: ",
      workId,
      " from server response: ",
      response
    );
    if (response.status === 200) {
      store.dispatch(addMoreCreatedItems(response.data)); // Dispatching to update Redux store
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching createdItems:", error);
    throw error;
  }
};

// Delete a createdItem
const deleteCreatedItemApi = (createdItemId) => {
  return async (dispatch) => {
    try {
      console.log(createdItemId);
      await axiosInstance.delete(`/created-items/${createdItemId}`);
      console.log("CreatedItem deleted successfully.");
      dispatch(deleteCreatedItem(createdItemId));
    } catch (error) {
      console.error("Error while deleting createdItem:", error);
      throw error;
    }
  };
};

// Delete a createdItem
const deleteMultipleCreatedItemsApi = (createdItemList) => {
  return async (dispatch) => {
    try {
      console.log(createdItemList);
      const response = await axiosInstance.delete(`/created-items/delete/items`, {
        data: createdItemList
      });
      const deletedIds = Array.isArray(response.data?.deleted)
        ? response.data.deleted
        : createdItemList;
      if (response.data?.forbidden?.length) {
        throw new Error("Some created items could not be deleted: permission denied.");
      }
      dispatch(deleteMoreCreatedItems(deletedIds));
      console.log("CreatedItem deleted successfully.");
    } catch (error) {
      console.error("Error while deleting createdItem:", error);
      throw error;
    }
  };
};

const updateCreatedItemApi = (createdItemId, updatedCreatedItemData) => {
  return async (dispatch) => {
    try {
      if (isFrontendOnly(updatedCreatedItemData)) return;
      await axiosInstance.put(
        `/created-items/${createdItemId}`,
        updatedCreatedItemData
      );
      dispatch(updateCreatedItem(updatedCreatedItemData));
    } catch (error) {
      console.error("Error while updating createdItem:", error);
      throw error;
    }
  };
};

const createCreatedItemApi = (createdItemData) => {
  return async (dispatch) => {
    try {
      if (isFrontendOnly(createdItemData)) {
        return createdItemData; // ❌ NEM mentjük
      }
      console.log("creating new createdItem");
      const response = await axiosInstance.post(
        `/created-items`,
        createdItemData
      );
      console.log("CreatedItem added successfully:", response);

      dispatch(addCreatedItem(response.data));
      return response.data;
    } catch (error) {
      console.error("Error while adding createdItem:", error);
      throw error;
    }
  };
};

const createMultipleCreatedItemsApi = (createdItemsDataList, { preprocess = false } = {}) => {
  return async (dispatch) => {
    try {
      const filtered = createdItemsDataList.filter((it) => !isFrontendOnly(it));
      if (filtered.length === 0) return [];
      const itemsLabeled = ensureDuplungLabels(filtered);
      console.log("Creating multiple createdItems: ", itemsLabeled);
      const response = await axiosInstance.post(
        `/created-items/items${preprocess ? "?preprocess=true" : ""}`,
        itemsLabeled
      );
      console.log("CreatedItems added successfully:", response);

      // Assuming the response.data is an array of newly created items
      if (response.status === 200) {
        store.dispatch(addMoreCreatedItems(response.data)); // Dispatching to update Redux store
      }

      return response.data;
    } catch (error) {
      console.error("Error while adding createdItems:", error);
      throw error;
    }
  };
};

const getCreatedItemOfUserAdminApi = (createdItemId) => {
  return async (dispatch) => {
    // A függvény visszatérési értéke egy aszinkron függvény
    try {
      const response = await axiosInstance.get(
        `/created-items/${createdItemId}`
      );
      //   dispatch(getCreatedItemSuccess(response.data)); // API válasz feldolgozása és diszpácselése a store-ba
      return response.data;
    } catch (error) {
      console.error("Error fetching createdItems:", error);
      throw error;
    }
  };
};

const updateMultipleCreatedItemsApi = (
  updatedItemsList,
  { requiresRegenerate = true } = {}
) => {
  return async (dispatch) => {
    try {
      const filtered = updatedItemsList.filter((it) => !isFrontendOnly(it));
      if (filtered.length === 0) return [];
      const response = await axiosInstance.put(
        `/created-items/items`,
        filtered,
        { params: { requiresRegenerate } }
      );
      console.log("Bulk update successful:", response.data);
      // For each updated dto, fire the single‐item update action
      response.data.forEach((dto) => {
        dispatch(updateCreatedItem(dto));
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk‐updating createdItems:", error);
      throw error;
    }
  };
};

const deleteCreatedItemsByObjectApi = (objectId) => {
  return async (dispatch) => {
    try {
      // 1) gather all itemIds in the store for this object
      const allItems = store.getState().createdItems || [];
      const idsToDelete = allItems
        .filter((it) => it.object?.objectId === objectId)
        .map((it) => it.itemId);

      // 2) call server
      await axiosInstance.delete(`/created-items/object/${objectId}`);

      // 3) purge from Redux
      dispatch(deleteMoreCreatedItems(idsToDelete));
      console.log(`Deleted ${idsToDelete.length} items for object ${objectId}`);
    } catch (error) {
      console.error("Error deleting createdItems by object:", error);
      throw error;
    }
  };
};
// --- EXCEL EXPORT (workhoz tartozó tételek) -------------------------------

function parseFilenameFromContentDisposition(cd) {
  if (!cd) return null;
  // pl: attachment; filename="work-123-items.xlsx"
  // vagy: attachment; filename*=UTF-8''work-123-items.xlsx
  let m = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (m && m[1]) return decodeURIComponent(m[1].replace(/["]/g, ""));
  m = cd.match(/filename\s*=\s*([^;]+)/i);
  if (m && m[1]) return m[1].trim().replace(/^["]+|["]+$/g, "");
  return null;
}

// --- segédek: szerepkör, work, fájlnév szanálás ----------------------------
function getCurrentUserRoleFromStore() {
  const st = store.getState?.() || {};
  return (
    st.user?.role ||
    st.currentUser?.role ||
    st.auth?.user?.role ||
    st.auth?.currentUser?.role ||
    st.loggedUser?.role ||
    st.profile?.role ||
    "user"
  );
}

function getWorkFromStore(workId) {
  const st = store.getState?.() || {};
  const works = st.works || [];
  return works.find((w) => String(w.workId) === String(workId)) || null;
}

function sanitizeFilename(name) {
  if (!name) return "export";
  // ékezetek le
  let out = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  // tiltott karakterek törlése, szóköz -> _
  out = out
    .replace(/[^a-zA-Z0-9\-_. ]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  // üres fallback
  return out || "export";
}

const exportCreatedItemsExcelForWorkApi = async (workId) => {
  try {
    const response = await axiosInstance.get(
      `/created-items/works/${workId}/export_excel`,
      { responseType: "blob" }
    );

    const role = String(getCurrentUserRoleFromStore()).toLowerCase();
    const work = getWorkFromStore(workId);
    const isCompanyOrAdmin =
      role === "admin" || role === "companyadmin" || role === "companyuser";
    const baseName = isCompanyOrAdmin
      ? work?.user?.name || work?.user?.username || "user"
      : work?.client?.name || "client";

    // ⬇⬇⬇ szerver-által küldött fejlécből fájlnév + content-type
    const cd = response.headers?.["content-disposition"] || "";
    const serverName = parseFilenameFromContentDisposition(cd); // már megvan a helpered
    const ct = response.headers?.["content-type"] || "application/octet-stream";

    // ha nincs szerver-név, ext a content-type alapján
    const ext = ct.includes("openxmlformats")
      ? "xlsx"
      : ct.includes("ms-excel")
        ? "xls"
        : "bin";
    const fallbackName = `${sanitizeFilename(
      baseName
    )}_${workId}_elemek.${ext}`;
    const finalName = serverName || fallbackName;

    const blob = new Blob([response.data], { type: ct });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = finalName; // ← NEM kényszerítünk .xls-t, azt használjuk, amit a szerver mond
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return finalName;
  } catch (error) {
    console.error("Error exporting Excel for work:", error);
    throw error;
  }
};

const getMdfCombinationsApi = async (workId) => {
  const res = await axiosInstance.get(
    `/created-items/works/${workId}/mdf_combinations`
  );
  return res.data;
};

const exportMdfExcelApi = async (workId, colorName, millingName) => {
  const res = await axiosInstance.get(
    `/created-items/works/${workId}/export_mdf`,
    {
      params: { colorName, millingName },
      responseType: "blob"
    }
  );
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  const disposition = res.headers["content-disposition"];
  const filename = disposition
    ? disposition.split("filename=")[1]?.replace(/"/g, "")
    : `mdf-${workId}-${colorName}-${millingName}.xls`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default {
  getAllCreatedItemsApi,
  getAllCreatedItemsForObjectApi,
  createMultipleCreatedItemsApi,
  getAllCreatedItemsForWorkApi,
  deleteCreatedItemApi,
  deleteMultipleCreatedItemsApi,
  updateCreatedItemApi,
  createCreatedItemApi,
  getCreatedItemOfUserAdminApi,
  updateMultipleCreatedItemsApi,
  deleteCreatedItemsByObjectApi,
  exportCreatedItemsExcelForWorkApi,
  getMdfCombinationsApi, // ← ÚJ
  exportMdfExcelApi // ← ÚJ
};
